/** @module Containers */
import React, { useCallback } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

import {
  CenterList,
  CustomGoogleMap,
  InputWithOverlappingLabel
} from '../../components'
import { CenterType, MemberCenter } from '../../types'
import { useGetMemberCentersQuery } from '../../redux/services/memberCenterService'
import { BlueMapIcon, PurpleMapIcon, RedMapIcon } from '../../assets'

/** @category Prop Types */
export type MemberCentersPropType = {
  onlyAssociate: boolean
}

export const MemberCenterMap: React.FC<MemberCentersPropType> = ({
  onlyAssociate = false
}: MemberCentersPropType) => {
  const [filteredMemberCenters, setFilteredMemberCenters] = React.useState<
    MemberCenter[] | []
  >([])
  const [displayCenterTypes, setDisplayCenterTypes] = React.useState<string[]>([
    'pediatric',
    'adult',
    'wholeLife'
  ])
  // const [filterValue, setFilterValue] = React.useState("");
  const {
    data: memberCenters,
    isFetching,
    isError,
    refetch
  } = useGetMemberCentersQuery()

  // Add isLoaded state to track Google Maps loading
  const [isMapLoading, setIsMapLoading] = React.useState(true)

  // Force refetch when onlyAssociate changes
  React.useEffect(() => {
    refetch()
  }, [onlyAssociate, refetch])

  const parsedMemberCenters = useCallback(() => {
    if (memberCenters) {
      if (onlyAssociate) {
        return memberCenters.filter((center) => center.associateCenter)
      }
      return memberCenters.filter((center) => center.associateCenter === false)
    }
    return []
  }, [memberCenters, onlyAssociate])

  React.useEffect(() => {
    setFilteredMemberCenters(parsedMemberCenters())
  }, [memberCenters, onlyAssociate, parsedMemberCenters])

  React.useEffect(() => {
    const tempArray: MemberCenter[] = []
    const copiedFilteredMemberCenters = parsedMemberCenters()
    if (displayCenterTypes.includes('adult') && copiedFilteredMemberCenters) {
      tempArray.push(
        ...tempArray,
        ...copiedFilteredMemberCenters.filter((center) => {
          return center.centerType === CenterType.Adult.toLowerCase()
        })
      )
    }
    if (
      displayCenterTypes.includes('pediatric') &&
      copiedFilteredMemberCenters
    ) {
      tempArray.push(
        ...tempArray,
        ...copiedFilteredMemberCenters.filter((center) => {
          return center.centerType === CenterType.Pediatric.toLowerCase()
        })
      )
    }
    if (
      displayCenterTypes.includes('wholeLife') &&
      copiedFilteredMemberCenters
    ) {
      tempArray.push(
        ...tempArray,
        ...copiedFilteredMemberCenters.filter((center) => {
          return center.centerType === CenterType.WholeLife.toLowerCase()
        })
      )
    }
    setFilteredMemberCenters(tempArray)
  }, [displayCenterTypes, parsedMemberCenters])

  function searchMemberCenters(searchText: string) {
    const searchedMemberCenters = parsedMemberCenters().filter((center) => {
      return (
        center.centerName.toLowerCase().includes(searchText.toLowerCase()) ||
        center.city.toLowerCase().includes(searchText.toLowerCase()) ||
        center.state.toLowerCase().includes(searchText.toLowerCase()) ||
        center.streetAddress.toLowerCase().includes(searchText.toLowerCase())
      )
    })
    setFilteredMemberCenters(searchedMemberCenters)
  }

  return (
    <>
      <main className="static-page-main py-4">
        <div className="flex justify-center mb-6">
          <h3 className="text-4xl font-bold text-[#3C2416]">
            Find {onlyAssociate ? 'an Associate ' : 'a '}Member Center
          </h3>
        </div>
        {onlyAssociate && (
          <p className="pb-4 max-w-1/2 mx-auto">
            Associate Centers are NASCC members who are currently working to
            become full Member Centers of NASCC by growing their available
            resources and treatments. Associate Centers are led by sickle cell
            disease experts with excellent treatment capabilities - please check
            their website for a better idea of the capabilities of the center.
          </p>
        )}
        <section className="max-w-5/6 mx-auto py-4">
          {isFetching ? (
            <SkeletonTheme baseColor="#EDE6DE" highlightColor="#9298a8">
              <div className="flex flex-col space-y-4">
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ height: '60vh' }}
                >
                  <Skeleton height="100%" width="100%" />
                </div>
                <div className="bg-white rounded-lg p-4 border border-[#9298a8]">
                  <Skeleton height={40} width={300} className="mb-4" />
                  <Skeleton height={48} width="100%" className="mb-4" />
                  <Skeleton height={30} width={200} className="mb-2" />
                  <div className="flex flex-col md:flex-row gap-3 w-full">
                    <Skeleton height={50} width="32%" />
                    <Skeleton height={50} width="32%" />
                    <Skeleton height={50} width="32%" />
                  </div>
                </div>
                <div className="px-8 pt-6">
                  <Skeleton height={40} width={250} className="mb-6" />
                  <Skeleton count={5} height={30} className="mb-2" />
                </div>
              </div>
            </SkeletonTheme>
          ) : isError ? (
            <div className="p-4 text-center text-red-600">
              Error retrieving centers. Please try refreshing the page.
            </div>
          ) : memberCenters && filteredMemberCenters ? (
            <>
              <div className={isMapLoading ? 'hidden' : 'block'}>
                <CustomGoogleMap
                  memberCenters={filteredMemberCenters}
                  onMapLoad={() => setIsMapLoading(false)}
                />
              </div>
              {isMapLoading && (
                <div
                  className="rounded-lg overflow-hidden"
                  style={{ height: '60vh' }}
                >
                  <SkeletonTheme baseColor="#EDE6DE" highlightColor="#9298a8">
                    <Skeleton height="100%" width="100%" />
                  </SkeletonTheme>
                </div>
              )}
              <div className="flex flex-col justify-between items-center space-y-4 text-xl pb-6 bg-gray-100 rounded-lg p-4 border border-[#9298a8] mt-4">
                <h4 className="text-2xl text-[#3C2416] font-medium">
                  Search and Filter Centers
                </h4>
                <InputWithOverlappingLabel
                  inputLabel="Search"
                  placeholderText="Search for a center..."
                  className="max-w-4xl w-full h-12 bg-[#EDE6DE] border border-[#9298a8] rounded-lg my-2"
                  inputClassName="h-12"
                  inputOnChange={(e) => {
                    searchMemberCenters(e.target.value)
                  }}
                />
                <div className="flex flex-col lg:flex-row w-full max-w-4xl justify-center">
                  <h4 className="text-xl text-[#3C2416] pb-2 pr-6 flex items-center">
                    Filter by center type:
                  </h4>
                  <div className="flex flex-col md:flex-row gap-3 w-full justify-center">
                    <button
                      className={`flex justify-start items-center bg-rose-300 rounded-lg p-3 hover:bg-rose-500 cursor-pointer ${
                        displayCenterTypes.length === 1 &&
                        displayCenterTypes[0] === 'adult'
                          ? 'ring-2 ring-[#3C2416]'
                          : ''
                      }`}
                      onClick={() => {
                        if (
                          displayCenterTypes.length === 1 &&
                          displayCenterTypes[0] === 'adult'
                        ) {
                          setDisplayCenterTypes([
                            'adult',
                            'pediatric',
                            'wholeLife'
                          ])
                        } else {
                          setDisplayCenterTypes(['adult'])
                        }
                      }}
                    >
                      <img
                        src={RedMapIcon}
                        alt="Adult center icon"
                        className="h-8 mr-2"
                      />{' '}
                      Adult Treatment Center
                    </button>
                    <button
                      className={`flex justify-start items-center bg-blue-300 rounded-lg p-3 hover:bg-blue-500 cursor-pointer ${
                        displayCenterTypes.length === 1 &&
                        displayCenterTypes[0] === 'pediatric'
                          ? 'ring-2 ring-[#3C2416]'
                          : ''
                      }`}
                      onClick={() => {
                        if (
                          displayCenterTypes.length === 1 &&
                          displayCenterTypes[0] === 'pediatric'
                        ) {
                          setDisplayCenterTypes([
                            'adult',
                            'pediatric',
                            'wholeLife'
                          ])
                        } else {
                          setDisplayCenterTypes(['pediatric'])
                        }
                      }}
                    >
                      <img
                        src={BlueMapIcon}
                        alt="Pediatric center icon"
                        className="h-8 mr-2"
                      />{' '}
                      Pediatric Treatment Center
                    </button>
                    <button
                      className={`flex justify-start items-center bg-purple-300 rounded-lg p-3 hover:bg-purple-500 cursor-pointer ${
                        displayCenterTypes.length === 1 &&
                        displayCenterTypes[0] === 'wholeLife'
                          ? 'ring-2 ring-[#3C2416]'
                          : ''
                      }`}
                      onClick={() => {
                        if (
                          displayCenterTypes.length === 1 &&
                          displayCenterTypes[0] === 'wholeLife'
                        ) {
                          setDisplayCenterTypes([
                            'adult',
                            'pediatric',
                            'wholeLife'
                          ])
                        } else {
                          setDisplayCenterTypes(['wholeLife'])
                        }
                      }}
                    >
                      <img
                        src={PurpleMapIcon}
                        alt="Whole life center icon"
                        className="h-8 mr-2"
                      />
                      Whole Life Treatment Center
                    </button>
                  </div>
                </div>
              </div>

              <div className="px-8 pt-10">
                {displayCenterTypes.length === 3 && (
                  <>
                    <h2 className="text-3xl font-bold tracking-tight mb-6 text-[#142841] border-b-2 border-[#9298a8] pb-2">
                      Treatment Centers
                    </h2>
                    {filteredMemberCenters &&
                      filteredMemberCenters.length === 0 &&
                      'No treatment centers found for this filter or type.'}
                    <CenterList memberCenters={filteredMemberCenters} />
                  </>
                )}
                {displayCenterTypes.length !== 3 &&
                  displayCenterTypes.includes('adult') && (
                    <>
                      <h2 className="text-3xl font-bold tracking-tight mb-6 text-[#142841] border-b-2 border-[#9298a8] pb-2">
                        Adult Treatment Centers
                      </h2>
                      <CenterList
                        memberCenters={memberCenters.filter((center) => {
                          return (
                            center.centerType === CenterType.Adult.toLowerCase()
                          )
                        })}
                      />
                      <br />
                    </>
                  )}
                {displayCenterTypes.length !== 3 &&
                  displayCenterTypes.includes('pediatric') && (
                    <>
                      <h2 className="text-3xl font-bold tracking-tight mb-6 text-[#142841] border-b-2 border-[#9298a8] pb-2">
                        Pediatric Treatment Centers
                      </h2>
                      <CenterList
                        memberCenters={memberCenters.filter((center) => {
                          return (
                            center.centerType ===
                            CenterType.Pediatric.toLowerCase()
                          )
                        })}
                      />
                      <br />
                    </>
                  )}
                {displayCenterTypes.length !== 3 &&
                  displayCenterTypes.includes('wholeLife') && (
                    <>
                      <h2 className="text-3xl font-bold tracking-tight mb-6 text-[#142841] border-b-2 border-[#9298a8] pb-2">
                        Whole Life Treatment Centers
                      </h2>
                      <CenterList
                        memberCenters={memberCenters.filter((center) => {
                          return (
                            center.centerType ===
                            CenterType.WholeLife.toLowerCase()
                          )
                        })}
                      />
                    </>
                  )}
              </div>
            </>
          ) : (
            'Error retrieving centers. Please try refreshing the page.'
          )}
        </section>
      </main>
    </>
  )
}
